import type { TranslationFunction } from '@/app/components/translations/Translation';
// eslint-disable-next-line import/no-cycle
import { AnalyticsFacilities } from './getFacilitiesOptions';
import { FACILITIES } from '../../../../../utils/FacilitiesType.ts';

export type FacilityType = { id: number; name: FACILITIES; text: string; analytics: AnalyticsFacilities };

export const defaultFacilities = [4, 0, 6, 5, 2, 1, 8, 7, 3, 9, 11]; // #10 (covid-19 related filter) was removed
export const generalFacilities = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 12, 13, 14]; // #10 (covid-19 related filter) was removed
export const familyFacilities = [15, 16, 17, 18, 19];
export const activityFacilities = [
  20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45,
];
export const wellnessFacilities = [46, 47, 48, 49, 50, 51, 52, 53];
export const foodFacilities = [54, 55, 56, 57, 58, 59, 60];

export const getOriginalFacilities = (t: TranslationFunction): FacilityType[] => [
  {
    id: 0,
    name: FACILITIES.FREE_WIFI,
    text: t('vacation.search.filters.facilities.options.freewifi'),
    analytics: AnalyticsFacilities.FreeWifi,
  },
  {
    id: 1,
    name: FACILITIES.PETS_ALLOWED,
    text: t('vacation.search.filters.facilities.options.pets'),
    analytics: AnalyticsFacilities.PetFriendly,
  },
  {
    id: 2,
    name: FACILITIES.DISABLE_GUEST,
    text: t('vacation.search.filters.facilities.options.disableguest'),
    analytics: AnalyticsFacilities.Accessibility,
  },
  {
    id: 3,
    name: FACILITIES.PARKING,
    text: t('vacation.search.filters.facilities.options.parking'),
    analytics: AnalyticsFacilities.Parking,
  },
  {
    id: 4,
    name: FACILITIES.SWIMMING_POOL,
    text: t('vacation.search.filters.facilities.options.swimmingpool'),
    analytics: AnalyticsFacilities.SwimmingPool,
  },
  {
    id: 5,
    name: FACILITIES.CHILDREN,
    text: t('vacation.search.filters.facilities.options.children'),
    analytics: AnalyticsFacilities.Children,
  },
  {
    id: 6,
    name: FACILITIES.SPA,
    text: t('vacation.search.filters.facilities.options.spa'),
    analytics: AnalyticsFacilities.SpaWellness,
  },
  {
    id: 7,
    name: FACILITIES.ADULTS_ONLY,
    text: t('vacation.search.filters.facilities.options.adultsonly'),
    analytics: AnalyticsFacilities.AdultsOnly,
  },
  {
    id: 8,
    name: FACILITIES.ENTERTAINMENT,
    text: t('vacation.search.filters.facilities.options.entertainment'),
    analytics: AnalyticsFacilities.Entertainment,
  },
  {
    id: 9,
    name: FACILITIES.SEAFRONT,
    text: t('vacation.search.filters.facilities.options.seafront'),
    analytics: AnalyticsFacilities.Seafront,
  },
  // {
  //   id: 10,
  //   name: FACILITIES.HEALTH_SAFETY',
  //   text: t('vacation.search.health.safety'),
  //   analytics: AnalyticsFacilities.Covid19Measures,
  // },
  {
    id: 11,
    name: FACILITIES.KITCHEN,
    text: t('vacation.search.filters.facilities.options.kitchen'),
    analytics: AnalyticsFacilities.Kitchen,
  },
];

export const getGeneralFacilities = (t: TranslationFunction): FacilityType[] => [
  ...getOriginalFacilities(t),
  {
    id: 12,
    name: FACILITIES.GENERAL_EV_CHARGE,
    text: t('vacation.oss.filters.general.ev_charging'),
    analytics: AnalyticsFacilities.EvCharge,
  },
  {
    id: 13,
    name: FACILITIES.GENERAL_FITNESS,
    text: t('vacation.oss.filters.general.fitness'),
    analytics: AnalyticsFacilities.Fitness,
  },
  {
    id: 14,
    name: FACILITIES.GENERAL_ROOFTOP_TERRACE,
    text: t('vacation.oss.filters.rooftop_terrace'),
    analytics: AnalyticsFacilities.RooftopTerrace,
  },
];

export const getFamilyFacilities = (t: TranslationFunction): FacilityType[] => [
  {
    id: 15,
    name: FACILITIES.FAMILY_CHILDREN_PLAYGROUND,
    text: t('vacation.oss.filters.family.playground'),
    analytics: AnalyticsFacilities.ChildrenPlayground,
  },
  {
    id: 16,
    name: FACILITIES.FAMILY_BABY_SITTING,
    text: t('vacation.oss.filters.family.baby_sitting'),
    analytics: AnalyticsFacilities.BabySitting,
  },
  {
    id: 17,
    name: FACILITIES.FAMILY_CHILDREN_CLUBS,
    text: t('vacation.oss.filters.family.club'),
    analytics: AnalyticsFacilities.ChildrenClubs,
  },
  {
    id: 18,
    name: FACILITIES.FAMILY_CHILDREN_MEAL,
    text: t('vacation.oss.filters.family.meal'),
    analytics: AnalyticsFacilities.ChildrenMeal,
  },
  {
    id: 19,
    name: FACILITIES.FAMILY_CHILDREN_POOL,
    text: t('vacation.oss.filters.family.pool'),
    analytics: AnalyticsFacilities.ChildrenPool,
  },
];

export const getActivityFacilities = (t: TranslationFunction): FacilityType[] => [
  {
    id: 20,
    name: FACILITIES.ACTIVITIES_HIKING,
    text: t('vacation.oss.filters.activities.hiking'),
    analytics: AnalyticsFacilities.Hiking,
  },
  {
    id: 21,
    name: FACILITIES.ACTIVITIES_FISHING,
    text: t('vacation.oss.filters.activities.fishing'),
    analytics: AnalyticsFacilities.Fishing,
  },
  {
    id: 22,
    name: FACILITIES.ACTIVITIES_CYCLING,
    text: t('vacation.oss.filters.activities.cycling'),
    analytics: AnalyticsFacilities.Cycling,
  },
  {
    id: 23,
    name: FACILITIES.ACTIVITIES_BOARD_GAMES,
    text: t('vacation.oss.filters.activities.board_games'),
    analytics: AnalyticsFacilities.BoardGames,
  },
  {
    id: 24,
    name: FACILITIES.ACTIVITIES_HORSE_RIDING,
    text: t('vacation.oss.filters.activities.horse_riding'),
    analytics: AnalyticsFacilities.HorseRiding,
  },
  {
    id: 25,
    name: FACILITIES.ACTIVITIES_GOLF,
    text: t('vacation.oss.filters.activities.golf'),
    analytics: AnalyticsFacilities.Golf,
  },
  {
    id: 26,
    name: FACILITIES.ACTIVITIES_BICYCLE_RENTAL,
    text: t('vacation.oss.filters.activities.bike_rental'),
    analytics: AnalyticsFacilities.BicycleRental,
  },
  {
    id: 27,
    name: FACILITIES.ACTIVITIES_CANOEING,
    text: t('vacation.oss.filters.activities.canoe'),
    analytics: AnalyticsFacilities.Canoe,
  },
  {
    id: 28,
    name: FACILITIES.ACTIVITIES_DIVING,
    text: t('vacation.oss.filters.activities.diving'),
    analytics: AnalyticsFacilities.Diving,
  },
  {
    id: 29,
    name: FACILITIES.ACTIVITIES_WINDSURFING,
    text: t('vacation.oss.filters.activities.windsurfing'),
    analytics: AnalyticsFacilities.WindSurfing,
  },
  {
    id: 30,
    name: FACILITIES.ACTIVITIES_WATER_PARK,
    text: t('vacation.oss.filters.activities.water_park'),
    analytics: AnalyticsFacilities.WaterPark,
  },
  {
    id: 31,
    name: FACILITIES.ACTIVITIES_SNORKELLING,
    text: t('vacation.oss.filters.activities.snorkelling'),
    analytics: AnalyticsFacilities.Snorkeling,
  },
  {
    id: 32,
    name: FACILITIES.ACTIVITIES_BOWLING,
    text: t('vacation.oss.filters.activities.bowling'),
    analytics: AnalyticsFacilities.Bowling,
  },
  {
    id: 33,
    name: FACILITIES.ACTIVITIES_BILLIARDS,
    text: t('vacation.oss.filters.activities.billiards'),
    analytics: AnalyticsFacilities.Billiards,
  },
  {
    id: 34,
    name: FACILITIES.ACTIVITIES_SCUBA,
    text: t('vacation.oss.filters.activities.scuba'),
    analytics: AnalyticsFacilities.Scuba,
  },
  {
    id: 35,
    name: FACILITIES.ACTIVITIES_MASSAGE,
    text: t('vacation.oss.filters.activities.massage'),
    analytics: AnalyticsFacilities.Massage,
  },
  {
    id: 36,
    name: FACILITIES.ACTIVITIES_WATER_SPORT,
    text: t('vacation.oss.filters.activities.water_sports'),
    analytics: AnalyticsFacilities.WaterSports,
  },
  {
    id: 37,
    name: FACILITIES.ACTIVITIES_EVENING_ENTERTAINMENT,
    text: t('vacation.oss.filters.activities.entertainment'),
    analytics: AnalyticsFacilities.EveningEntertainment,
  },
  {
    id: 38,
    name: FACILITIES.ACTIVITIES_TENNIS_COURT,
    text: t('vacation.oss.filters.activities.tennis'),
    analytics: AnalyticsFacilities.TennisCourt,
  },
  {
    id: 39,
    name: FACILITIES.ACTIVITIES_ARCADE_ROOM,
    text: t('vacation.oss.filters.activities.arcade'),
    analytics: AnalyticsFacilities.ArcadeRoom,
  },
  {
    id: 40,
    name: FACILITIES.ACTIVITIES_KAYAKING,
    text: t('vacation.oss.filters.activities.kayak'),
    analytics: AnalyticsFacilities.Kayaking,
  },
  {
    id: 41,
    name: FACILITIES.ACTIVITIES_ECOTOURS,
    text: t('vacation.oss.filters.activities.ecotour'),
    analytics: AnalyticsFacilities.EcoTours,
  },
  {
    id: 42,
    name: FACILITIES.ACTIVITIES_YOGA,
    text: t('vacation.oss.filters.activities.yoga'),
    analytics: AnalyticsFacilities.Yoga,
  },
  {
    id: 43,
    name: FACILITIES.ACTIVITIES_NIGHTCLUB,
    text: t('vacation.oss.filters.activities.nightclub'),
    analytics: AnalyticsFacilities.NightClub,
  },
  {
    id: 44,
    name: FACILITIES.ACTIVITIES_DARTS,
    text: t('vacation.oss.filters.activities.darts'),
    analytics: AnalyticsFacilities.Darts,
  },
  {
    id: 45,
    name: FACILITIES.ACTIVITIES_PLAY_AREA,
    text: t('vacation.oss.filters.activities.indoor_games'),
    analytics: AnalyticsFacilities.IndoorPlayArea,
  },
];

export const getWellnessFacilities = (t: TranslationFunction): FacilityType[] => [
  {
    id: 46,
    name: FACILITIES.SPA_INDOOR_POOL,
    text: t('vacation.oss.filters.wellness.indoor_pool'),
    analytics: AnalyticsFacilities.IndoorPool,
  },
  {
    id: 47,
    name: FACILITIES.SPA_ROOFTOP_POOL,
    text: t('vacation.oss.filters.wellness.rooftop_pool'),
    analytics: AnalyticsFacilities.RooftopPool,
  },
  {
    id: 48,
    name: FACILITIES.SPA_HAMMAM,
    text: t('vacation.oss.filters.wellness.hammam'),
    analytics: AnalyticsFacilities.Hammam,
  },
  {
    id: 49,
    name: FACILITIES.SPA_SAUNA,
    text: t('vacation.oss.filters.wellness.sauna'),
    analytics: AnalyticsFacilities.Sauna,
  },
  {
    id: 50,
    name: FACILITIES.SPA_STEAM_ROOM,
    text: t('vacation.oss.filters.wellness.steam_room'),
    analytics: AnalyticsFacilities.SteamRoom,
  },
  {
    id: 51,
    name: FACILITIES.SPA_SALT_POOL,
    text: t('vacation.oss.filters.wellness.salt_pool'),
    analytics: AnalyticsFacilities.SaltPool,
  },
  {
    id: 52,
    name: FACILITIES.SPA_INFINITY_POOL,
    text: t('vacation.oss.filters.wellness.infinity_pool'),
    analytics: AnalyticsFacilities.InfinityPool,
  },
  {
    id: 53,
    name: FACILITIES.SPA_WATER_SLIDE,
    text: t('vacation.oss.filters.wellness.waterslide'),
    analytics: AnalyticsFacilities.WaterSlide,
  },
];

export const getFoodFacilities = (t: TranslationFunction): FacilityType[] => [
  {
    id: 54,
    name: FACILITIES.FOOD_SUN_TERRACE,
    text: t('vacation.oss.filters.food.sun_terrace'),
    analytics: AnalyticsFacilities.SunTerrace,
  },
  {
    id: 55,
    name: FACILITIES.FOOD_PICNIC,
    text: t('vacation.oss.filters.food.picnic'),
    analytics: AnalyticsFacilities.Picnic,
  },
  {
    id: 56,
    name: FACILITIES.FOOD_BAR,
    text: t('vacation.oss.filters.food.bar'),
    analytics: AnalyticsFacilities.Bar,
  },
  {
    id: 57,
    name: FACILITIES.FOOD_POOL_BAR,
    text: t('vacation.oss.filters.food.poolbar'),
    analytics: AnalyticsFacilities.Pool_bar,
  },
  {
    id: 58,
    name: FACILITIES.FOOD_RESTAURANT,
    text: t('vacation.oss.filters.food.restaurant'),
    analytics: AnalyticsFacilities.Restaurant,
  },
  {
    id: 59,
    name: FACILITIES.FOOD_BBQ,
    text: t('vacation.oss.filters.food.bbq'),
    analytics: AnalyticsFacilities.Bbq,
  },
  {
    id: 60,
    name: FACILITIES.FOOD_MINIMARKET,
    text: t('vacation.oss.filters.food.minimarket'),
    analytics: AnalyticsFacilities.MiniMarket,
  },
];
